/**
 * Session WebSocket Event Hook
 *
 * File: Mythbound.App/src/hooks/useSessionWebSocket.ts
 * Created: 2026-01-06
 * Last Modified: 2026-01-06
 *
 * PURPOSE:
 * React hook for subscribing to session-related WebSocket events.
 * Integrates with SessionContext to update state in real-time.
 *
 * EVENTS HANDLED (INSTANT):
 * - session.started - Session began (multi-platform sync)
 * - session.ended - Session ended
 * - session.paused - Session paused (future)
 * - session.resumed - Session resumed (future)
 * - participant.joined - Participant joined session
 * - participant.left - Participant left session
 *
 * POLLED STATE (3-second interval):
 * - HP changes
 * - Inventory updates
 * - Equipment changes
 * (Handled by SessionContext polling, not WebSocket)
 *
 * USAGE:
 * ```tsx
 * function SessionView() {
 *   const { session } = useSession();
 *   useSessionWebSocket(session?.campaign_id);
 *   // Component automatically updates on WebSocket events
 * }
 * ```
 *
 * Pipeline: WebSocket events → useSessionWebSocket → SessionContext → UI updates
 * Dependencies: React, websocketService, useSession context
 */

import { useEffect, useRef } from 'react';
import { useSession } from '../context/SessionContext';
import { toastService } from '../services/toast/toastService';
import { webSocketService } from '../services/websocket/websocketService';

export const useSessionWebSocket = (campaignId?: string) => {
  const { refreshState } = useSession();

  // Store handlers in refs to avoid re-subscribing on every render
  const handlersRef = useRef<Record<string, (data: any) => void>>({});

  useEffect(() => {
    // Skip if no campaign ID
    if (!campaignId) return;

    /**
     * Handle session.started event (INSTANT)
     * Triggered when session starts from webapp or Discord
     * 
     * MULTI-PLATFORM SYNC:
     * - Webapp "Play Myth" button triggers this
     * - Discord /mythbound start triggers this
     * - Both users see instant notification
     */
    handlersRef.current['session.started'] = (data: any) => {
      console.log('[Session WS] session.started:', data);
      if (data.campaign_id === campaignId) {
        // Refresh session state immediately
        refreshState();

        const source = data.source === 'webapp' ? 'webapp' : 'Discord';
        toastService.success(
          `Session started${data.source !== 'webapp' ? ` on ${source}` : ''}! Join now.`
        );
        
        // TODO: Show banner notification
        // "Session Active" banner at top of page
        
        // TODO: Auto-navigate to SessionView if user on campaign page
        // window.location.href = `/session/${data.session_id}`;
      }
    };

    /**
     * Handle session.ended event (INSTANT)
     * Triggered when session ends
     */
    handlersRef.current['session.ended'] = (data: any) => {
      console.log('[Session WS] session.ended:', data);
      if (data.campaign_id === campaignId || data.session_id) {
        // Refresh session state immediately
        refreshState();

        const duration = data.duration_minutes 
          ? `Duration: ${Math.floor(data.duration_minutes / 60)}h ${data.duration_minutes % 60}m`
          : '';
        toastService.info(`Session ended. ${duration}`, 5000);
        
        // TODO: Show recap modal
        // Display session summary, duration, events
        
        console.log(
          `Session ended: ${data.session_id}, Duration: ${data.duration_minutes} minutes`
        );
      }
    };

    /**
     * Handle participant.joined event (INSTANT)
     * Triggered when participant joins active session
     */
    handlersRef.current['participant.joined'] = (data: any) => {
      console.log('[Session WS] participant.joined:', data);
      if (data.campaign_id === campaignId) {
        toastService.info(`${data.screen_name || 'A player'} joined the session`);
        
        // Refresh session state to update participant list
        refreshState();
      }
    };

    /**
     * Handle participant.left event (INSTANT)
     * Triggered when participant leaves active session
     */
    handlersRef.current['participant.left'] = (data: any) => {
      console.log('[Session WS] participant.left:', data);
      if (data.campaign_id === campaignId) {
        toastService.info(`${data.screen_name || 'A player'} left the session`);
        
        // Refresh session state to update participant list
        refreshState();
      }
    };

    /**
     * Handle session.paused event (FUTURE)
     * Triggered when the session lifecycle becomes paused
     */
    handlersRef.current['session.paused'] = (data: any) => {
      console.log('[Session WS] session.paused:', data);
      if (data.session_id) {
        // Refresh authoritative lifecycle and mode settings.
        refreshState();
      }
    };

    /**
     * Handle session.resumed event (FUTURE)
     * Triggered when the session lifecycle resumes
     */
    handlersRef.current['session.resumed'] = (data: any) => {
      console.log('[Session WS] session.resumed:', data);
      if (data.session_id) {
        // Refresh authoritative lifecycle and mode settings.
        refreshState();
      }
    };

    // Subscribe to all events
    Object.entries(handlersRef.current).forEach(([event, handler]) => {
      webSocketService.on(event, handler);
    });

    // Cleanup: unsubscribe from all events
    return () => {
      Object.entries(handlersRef.current).forEach(([event, handler]) => {
        webSocketService.off(event, handler);
      });
    };
  }, [campaignId, refreshState]);
};

